/* eslint-disable react-hooks/rules-of-hooks */
import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { createEvent } from "../firebaseFunctions/createEvent/createEvent";
import { fetchPublishedEventById } from "../firebaseFunctions/mpsDashboard/mpsDashboard";
import { getMPSEventAppliedStatusById } from "../firebaseFunctions/getMPSEventAppliedStatus/getMPSEventAppliedStatus";
import { useAuth } from "./AuthContext";
import { collection, query, where, getDocs, Timestamp } from "firebase/firestore";
import { db } from "../firebaseFunctions/config";
import { subscriptionService } from "../services/SubscriptionService";

export const CreateEventContext = createContext();

export const CreateEventProvider = ({ children }) => {
  const { user, subscriptionTier } = useAuth();
  const UserID = user?.uid;

  // Define initialState FIRST
  const initialState = {
    published: false,
    userId: "",
    eventName: "",
    location: "",
    description: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    recurringEvent: false,
    deadlineDate: "",
    deadlineTime: "",
    loadInDate: "",
    loadInTime: "",
    loadoutDate: "",
    loadoutTime: "",
    socialMediaHandles: "" || {},
    eoEventWebsiteURL: "",
    sellerTypes: [],
    otherSellerType: "",
    sellerCapacity: 0,
    documents: [{ documentName: "", requiredFrom: "" }],
    addRegistrationFee: true,
    registrationFee: 0,
    absorbServiceFee: false,
    serviceFee: 0,
    transactionFees: 0,
    transactionFeePercentage: 0,
    paymentTiming: "after_approval",
    questions: {},
    typeOfQuestionList: [{}],
    faqList: [{ question: "", answer: "" }],
    activeStep: null,
    eventAppliedStatus: null,
    eventCoverPhoto: null,
    eventDocuments: [],
    eventMap: null,
    eventType: "Festival",
    treeDonationEnabled: false,
    treeDonationRequired: false,
    treeContributionCount: 0,
    treeContributionTotal: 0,
  };

  // Now you can use it here
  const [eventData, setEventData] = useState(initialState);
  const eventDataRef = useRef(eventData);
  eventDataRef.current = eventData;
  const getEventData = useCallback(async (eventId) => {
    try {
      const response = await fetchPublishedEventById(eventId);
      if (response && JSON.stringify(response) !== JSON.stringify(eventDataRef.current)) {
        // Update event data if changed
        updateEventData(response);

        // Fetch the event applied status
        const eventAppliedStatusResponse = await getMPSEventAppliedStatusById(UserID, eventId);
        if (eventAppliedStatusResponse.success === true) {
          const eventDetails = eventAppliedStatusResponse.eventDetails[0];
          if (eventDetails) {
            // Extract relevant fields from eventDetails
            const {
              applicationId = null,
              applicationStatus = null,
              paymentStatus = null,
              assignedBooth = null,
            } = eventDetails;

            // Update event data with the new fields
            updateEventData({
              ...response,
              applicationId,
              applicationStatus,
              paymentStatus,
              assignedBooth,
            });
          }
        }
        return response;
      }
    } catch (error) {
      console.error("Error getting event data by ID: ", error);
    }
  }, [UserID]);

  // Function to update event data
  const updateEventData = (newData) => {
    setEventData((prevData) => ({ ...prevData, ...newData }));
  };
  const resetEventData = () => {
    setEventData(initialState);
  }

  const saveEventData = async (userId = null, isDraft = false, directData = null) => {
    try {
      const effectiveUserId = userId || UserID;

      // Enforce monthly event limit for free tier (only when publishing, not drafting)
      if (!isDraft && effectiveUserId) {
        const plan = subscriptionService.getPlan(subscriptionTier);
        const eventLimit = plan?.eventLimit;

        if (eventLimit !== null && eventLimit !== undefined) {
          const startOfMonth = new Date();
          startOfMonth.setDate(1);
          startOfMonth.setHours(0, 0, 0, 0);

          const eventsThisMonth = await getDocs(
            query(
              collection(db, 'events'),
              where('userId', '==', effectiveUserId),
              where('isDraft', '==', false),
              where('createdAt', '>=', Timestamp.fromDate(startOfMonth))
            )
          );

          // Don't count the current event if it's being re-published (has an existing eventId)
          const existingEventId = (directData || eventData).eventId;
          const newEventsCount = eventsThisMonth.docs.filter(
            d => d.id !== existingEventId
          ).length;

          if (newEventsCount >= eventLimit) {
            return {
              success: false,
              limitReached: true,
              message: `You've reached the ${eventLimit} event/month limit on the Basic plan. Upgrade to Pro for unlimited events.`
            };
          }
        }
      }

      const dataToSave = directData || eventData;
      const eventDataWithUserId = {
        ...dataToSave,
        userId: effectiveUserId || dataToSave.userId,
      };

      const response = await createEvent(eventDataWithUserId, isDraft);
      
      if (response && response.success) {
        return { success: true, message: response.message, eventId: response.eventId };
      } else {
        console.error("Event creation failed:", response?.message);
        return { success: false, message: response?.message || "Failed to save event" };
      }
    } catch (error) {
      console.error("Error saving event data: ", error);
      return { success: false, message: error.message || "An error occurred" };
    }
  };
  // const getEventData = async (eventId) => {
  //   try {
  //     const response = await fetchPublishedEventById(eventId);
  //     console.log("eventData",response);
  //     if(response){
  //       // updateEventData(response);
  //       return true;
  //     }
  //   } catch (error) {
  //     console.error("Error getting event data by ID: ", error);
  //     return false;
  //   }
  // };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <CreateEventContext.Provider
      value={{ eventData, updateEventData, saveEventData, resetEventData, getEventData }}
    >
      {children}
    </CreateEventContext.Provider>
  );
};

export const useCreateEventContext = () => {
  return useContext(CreateEventContext);
};


